import React, { useCallback, useMemo } from 'react';

import clsx from 'clsx';

import classes from './dots.module.scss';

type DotsProps = {
  currentIndex: number;
  itemsLength: number;
  className?: string;
  onClick?: (any: any) => void;
};

type SingleDotProps = {
  index: number;
  onClick: DotsProps['onClick'];
  active?: boolean;
};

const SingleDot = ({ index, active, onClick }: SingleDotProps) => {
  const onDotClick = useCallback(() => {
    if (onClick) onClick(index);
  }, [index]);

  const singleDot = useMemo(
    () => (
      <div onClick={onDotClick} className={classes.dotWrapper}>
        <div className={clsx(classes.dot, active && classes.active)}></div>
      </div>
    ),
    [active],
  );
  return singleDot;
};

export const Dots = ({
  currentIndex = 0,
  itemsLength,
  className,
  onClick,
}: DotsProps) => {
  // handle infinity - currentIndex can be -1 OR items.length
  const calculatedIndex = useMemo(() => {
    // handles right side
    if (currentIndex === itemsLength) return 0;
    // handles left side
    else if (currentIndex === -1) return itemsLength - 1;
    return currentIndex;
  }, [currentIndex, itemsLength]);

  const translateMultiplier = useMemo(() => {
    if (calculatedIndex < 3 || itemsLength < 6) return 0;
    else if (calculatedIndex >= itemsLength - 2) return itemsLength - 5;
    return calculatedIndex - 2;
  }, [calculatedIndex, itemsLength]);

  const dots = useMemo(() => {
    const renderedDots = [];
    for (let i = 0; i < itemsLength; i++) {
      const isCurrentIndex = i === calculatedIndex;

      renderedDots?.push(
        <SingleDot
          key={i}
          index={i}
          onClick={onClick}
          active={isCurrentIndex}
        />,
      );
    }
    return renderedDots;
  }, [calculatedIndex, itemsLength]);

  const wrapperWidth = useMemo(
    () => Math.min(itemsLength * 20, 100),
    [itemsLength],
  );

  return (
    <div
      style={{ width: `${wrapperWidth}px` }}
      className={clsx(className, classes.dotsWrapper)}
    >
      <div
        // ¯\_(ツ)_/¯
        style={{
          transform: `translate3d(${translateMultiplier * -20}px, 0px, 0px)`,
        }}
        className={clsx(classes.dotsInnerWrapper)}
      >
        {dots}
      </div>
    </div>
  );
};
